<template>
  <div v-if="visible" class="font-poppins">
    <!-- Panel: a card above the launcher on desktop, full screen on mobile -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 translate-y-4"
      leave-active-class="transition duration-150 ease-in"
      leave-to-class="opacity-0 translate-y-4"
    >
      <div
        v-if="chat.isOpen"
        class="fixed z-[70] inset-0 sm:inset-auto sm:right-4 sm:bottom-[calc(var(--bottom-nav-height,0px)+5.5rem)] sm:w-[380px] sm:h-[560px] sm:max-h-[calc(100vh-8rem)] sm:rounded-2xl sm:shadow-2xl overflow-hidden sm:border sm:border-gray-200 dark:sm:border-white/10"
        role="dialog"
        aria-label="Chat s podporou"
      >
        <ChatPanel>
          <template #header-actions>
            <button
              type="button"
              class="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/15 transition-colors"
              aria-label="Zavrieť chat"
              @click="chat.isOpen = false"
            >
              <X class="w-5 h-5" />
            </button>
          </template>
        </ChatPanel>
      </div>
    </Transition>

    <!-- Launcher -->
    <button
      v-show="!chat.isOpen || !isMobile"
      type="button"
      class="fixed z-[60] right-4 bottom-[calc(var(--bottom-nav-height,0px)+1rem)] h-14 rounded-full shadow-lg flex items-center justify-center gap-2 bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] text-white transition duration-200"
      :class="[showLabel ? 'pl-5 pr-6' : 'w-14', { 'max-sm:hidden': hideLauncherOnMobile, 'opacity-0 translate-y-4 pointer-events-none': hideAtFooter }]"
      :inert="hideAtFooter"
      :aria-label="chat.isOpen ? 'Zavrieť chat' : 'Napíšte nám'"
      @click="chat.toggle()"
    >
      <X v-if="chat.isOpen" class="w-6 h-6" />
      <MessagesSquare v-else class="w-6 h-6" />
      <span v-if="showLabel" class="text-[15px] font-semibold whitespace-nowrap">Napíšte nám</span>
      <span
        v-if="chat.unreadCount > 0 && !chat.isOpen"
        class="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center ring-2 ring-white"
      >
        {{ chat.unreadCount > 9 ? '9+' : chat.unreadCount }}
      </span>
    </button>
  </div>
</template>

<script setup>
import { computed, ref, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { MessagesSquare, X } from 'lucide-vue-next'
import { useChatStore } from '@/stores/chat'
import brand from '@/brand'
import ChatPanel from './ChatPanel.vue'

const route = useRoute()
const chat = useChatStore()

const isMobile = ref(false)
const updateMobile = () => { isMobile.value = window.matchMedia('(max-width: 639px)').matches }

// Pages that show the chat themselves, or are not part of the shop
const visible = computed(() => {
  const path = route.path
  if (!route.matched.length) return false
  if (path.startsWith('/admin') || path.startsWith('/auth') || path.startsWith('/user/messages')) return false
  if (route.name === 'maintenance') return false
  return true
})

// fixed bottom bars (product CTA, guide, cart, checkout, account tabs) own the mobile corner
const MOBILE_BAR_ROUTES = ['product.detail', 'guide']
const hideLauncherOnMobile = computed(() =>
  MOBILE_BAR_ROUTES.includes(route.name)
    || ['/user', '/checkout', '/cart'].some(prefix => route.path.startsWith(prefix))
)

// brands can turn the bubble into a "Napíšte nám" pill on some pages (brand.chatLabelRoutes)
const showLabel = computed(() => !chat.isOpen && (brand.chatLabelRoutes || []).includes(route.name))

// the pill steps aside once the page footer scrolls into view
const footerInView = ref(false)
const hideAtFooter = computed(() => showLabel.value && footerInView.value)
let footerObserver = null
const observeFooter = () => {
  footerObserver?.disconnect()
  footerInView.value = false
  const footer = document.querySelector('footer')
  if (!footer) return
  footerObserver = new IntersectionObserver(([entry]) => { footerInView.value = entry.isIntersecting })
  footerObserver.observe(footer)
}
// a new layout brings its own footer
watch(() => route.name, () => nextTick(observeFooter))

watch(visible, (isVisible) => {
  if (!isVisible) chat.isOpen = false
})

// no page scroll behind the full-screen chat on phones
watch(() => chat.isOpen && isMobile.value, (locked) => {
  document.body.style.overflow = locked ? 'hidden' : ''
})

onMounted(() => {
  updateMobile()
  window.addEventListener('resize', updateMobile)
  observeFooter()
  chat.schedule()
  chat.poll()
})
onUnmounted(() => {
  window.removeEventListener('resize', updateMobile)
  footerObserver?.disconnect()
})
</script>
